import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * POSER, DÉPLACER, REDIMENSIONNER UNE ZONE — SANS AUCUNE BIBLIOTHÈQUE.
 *
 * ⚠️ POURQUOI PAS `draggable`, dnd-kit, react-dnd NI react-draggable
 *
 *   HTML5 draggable   fait pour TRANSFÉRER une donnée, pas pour positionner. Image
 *                     fantôme non maîtrisée, et il ne sait PAS redimensionner. Il reste
 *                     parfait là où il est déjà : déposer un fichier PDF.
 *   dnd-kit/react-dnd faits pour des listes triables et des cibles de dépôt. Ici il n'y a
 *                     ni liste ni cible : il y a un plan et des coordonnées.
 *   react-draggable   ferait le travail, au prix d'une dépendance pour cinquante lignes.
 *   Pointer Events    souris, trackpad, stylet et tactile par le MÊME code, et
 *                     `setPointerCapture` supprime le « le curseur sort du cadre, ça se
 *                     fige » — le défaut le plus agaçant d'un glisser-déposer fait main.
 *
 * ⚠️ `getBoundingClientRect()` DE L'IMAGE EST LA SEULE MESURE PRISE
 *
 * Et elle rend directement des FRACTIONS. L'écran ne connaît que des pixels CSS, dont le
 * nombre dépend du zoom, de la largeur du conteneur et du DPI : une fraction est la seule
 * grandeur qu'il puisse produire sans connaître `page.rect`. Le front n'a jamais un point
 * PDF en main, et la conversion vit à un seul endroit, côté serveur.
 *
 * ⚠️ LE CLAVIER N'EST PAS UN SUPPLÉMENT D'ACCESSIBILITÉ
 *
 * C'est aussi la précision : personne ne pose une zone au pixel à la souris. Les flèches
 * déplacent d'un pixel, Maj+flèches de dix. C'est le geste qu'on fait vraiment quand on
 * veut que le nom tombe exactement sur la ligne.
 */

// Le pas de la grille d'aimantation, en fraction de page. 0,5 % d'une A4 fait ~3 pt de
// large : assez fin pour ne pas se sentir contraint, assez gros pour aligner deux zones
// entre elles sans y penser. Alt libère l'aimant.
const PAS_GRILLE = 0.005;

// La distance sous laquelle une zone se colle à une ligne de texte du document, en
// fraction de la hauteur. ~0,5 % d'une A4 fait 4 pt, soit à peu près une demi-ligne.
const AIMANT_LIGNE = 0.005;

// Le déplacement au clavier, en fraction. Calé sur une A4 : 1 px ≈ 1/842 de la hauteur.
const PAS_CLAVIER = 1 / 842;
const PAS_CLAVIER_RAPIDE = 10 / 842;

// La taille par défaut d'une zone qu'on vient de déposer, et son plancher. Une zone plus
// petite que ça n'est pas visible, donc pas sélectionnable, donc perdue.
const LARGEUR_DEFAUT = 0.30;
const HAUTEUR_DEFAUT = 0.025;
const DIMENSION_MIN = 0.008;

// La pile d'annulation. Vingt états couvrent largement une session de réglage, et bornent
// la mémoire d'un écran qui reste ouvert une demi-journée.
const PROFONDEUR_ANNULATION = 20;

const borne = (valeur, mini, maxi) => Math.max(mini, Math.min(maxi, valeur));

/** La valeur aimantée à la grille, sauf si l'utilisateur tient Alt. */
function aimanter(valeur, libre) {
  return libre ? valeur : Math.round(valeur / PAS_GRILLE) * PAS_GRILLE;
}

/**
 * Le haut de la zone, collé à la ligne de texte la plus proche si elle est assez près.
 *
 * ⚠️ ON AIMANTE LA BASE, PAS LE HAUT. Poser un champ « sur la ligne du nom » veut dire
 * que sa BASE tombe sur cette ligne — c'est là que le texte s'écrit. Aimanter le haut
 * ferait flotter la valeur une ligne au-dessus, ce qui est précisément le geste raté
 * qu'on cherche à empêcher.
 */
function aimanterALaLigne(y, hauteur, lignes, libre) {
  if (libre || !lignes?.length) return y;
  const base = y + hauteur;
  let meilleure = null;
  for (const ligne of lignes) {
    const ecart = Math.abs(ligne - base);
    if (ecart <= AIMANT_LIGNE && (meilleure === null || ecart < Math.abs(meilleure - base))) {
      meilleure = ligne;
    }
  }
  return meilleure === null ? y : meilleure - hauteur;
}

/** Les huit poignées, dans le sens horaire depuis le coin haut-gauche. */
export const POIGNEES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

function redimensionner(zone, poignee, dx, dy) {
  let { x, y, largeur, hauteur } = zone;
  if (poignee.includes('w')) { x += dx; largeur -= dx; }
  if (poignee.includes('e')) { largeur += dx; }
  if (poignee.includes('n')) { y += dy; hauteur -= dy; }
  if (poignee.includes('s')) { hauteur += dy; }
  // Une zone retournée n'a pas de sens : on bloque au plancher plutôt que de laisser la
  // largeur devenir négative et la zone se replier sur elle-même.
  if (largeur < DIMENSION_MIN) { largeur = DIMENSION_MIN; x = zone.x; }
  if (hauteur < DIMENSION_MIN) { hauteur = DIMENSION_MIN; y = zone.y; }
  return { x, y, largeur, hauteur };
}

export default function usePlacementDeZones(zonesInitiales, { lignesParPage } = {}) {
  const [zones, poserZones] = useState(zonesInitiales || []);
  const [selection, setSelection] = useState(null);
  // La zone que le doigt tient EN CE MOMENT. Elle n'existait pas, et c'est ce qui
  // empêchait l'écran de montrer les lignes du document pendant le geste : l'aimant
  // agissait sans que rien ne dise vers quoi il tirait.
  const [enDeplacement, setEnDeplacement] = useState(null);
  const pile = useRef([]);
  const geste = useRef(null);

  // Le jeu venu du serveur remplace l'état local, et vide la pile : annuler après un
  // rechargement ramènerait des zones que le serveur ne connaît plus.
  useEffect(() => {
    poserZones(zonesInitiales || []);
    pile.current = [];
  }, [zonesInitiales]);

  const memoriser = useCallback((avant) => {
    pile.current = [...pile.current, avant].slice(-PROFONDEUR_ANNULATION);
  }, []);

  const modifier = useCallback((majParZone) => {
    poserZones((actuelles) => actuelles.map(majParZone));
  }, []);

  /** Pose une zone neuve à la fraction donnée. La pastille tirée depuis la palette. */
  const poser = useCallback((champ, x, y, page = 0) => {
    poserZones((actuelles) => {
      memoriser(actuelles);
      const zone = {
        // `crypto.randomUUID` n'existe pas partout ; l'identifiant est de toute façon
        // revalidé par le serveur, qui en attribue un s'il n'en reçoit pas.
        id: `z${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`,
        champ,
        page,
        x: borne(x - LARGEUR_DEFAUT / 2, 0, 1 - LARGEUR_DEFAUT),
        y: borne(y - HAUTEUR_DEFAUT / 2, 0, 1 - HAUTEUR_DEFAUT),
        largeur: LARGEUR_DEFAUT,
        hauteur: HAUTEUR_DEFAUT,
        taille: 9,
        alignement: 'gauche',
        unite: 'fraction_page',
        origine: 'haut_gauche',
      };
      setSelection(zone.id);
      return [...actuelles, zone];
    });
  }, [memoriser]);

  const supprimer = useCallback((id) => {
    poserZones((actuelles) => {
      memoriser(actuelles);
      return actuelles.filter((z) => z.id !== id);
    });
    setSelection((s) => (s === id ? null : s));
  }, [memoriser]);

  const annuler = useCallback(() => {
    if (!pile.current.length) return;
    const precedent = pile.current[pile.current.length - 1];
    pile.current = pile.current.slice(0, -1);
    poserZones(precedent);
  }, []);

  /**
   * Démarre un déplacement ou un redimensionnement.
   *
   * `mesurer()` doit rendre le `getBoundingClientRect()` de l'IMAGE de la page — pas du
   * conteneur, qui porte une bordure et un padding : deux ou trois pixels d'écart
   * suffisent à décaler toutes les zones d'un demi-millimètre sur le PDF.
   */
  const commencer = useCallback((evenement, id, poignee, mesurer) => {
    const cadre = mesurer();
    if (!cadre?.width || !cadre?.height) return;
    evenement.preventDefault();
    evenement.stopPropagation();
    // ⚠️ `setPointerCapture` : sans lui, sortir du cadre pendant le glisser fige la zone
    // là où le curseur a quitté l'élément, et le geste doit être recommencé.
    evenement.currentTarget.setPointerCapture?.(evenement.pointerId);

    // ⚠️ ET LE FOCUS, À LA MAIN. Le `preventDefault` ci-dessus — indispensable pour que
    // le navigateur ne sélectionne pas le texte pendant le glisser — supprime AUSSI le
    // focus que le clic aurait posé. Sans cette ligne, le `keydown` part au `body`, qui
    // n'est pas un descendant de la surface : les flèches ne déplacent rien et Ctrl+Z
    // n'annule rien, alors que la zone est visiblement sélectionnée.
    //
    // Défaut mesuré en recette d'écran le 02/09 : la zone se posait, se déplaçait à la
    // souris, et le clavier restait muet sans le moindre message.
    evenement.currentTarget.closest?.('[data-zone-id]')?.focus?.();

    setSelection(id);
    setEnDeplacement(id);
    poserZones((actuelles) => { memoriser(actuelles); return actuelles; });
    const depart = zones.find((z) => z.id === id);
    geste.current = {
      id, poignee, cadre,
      xDepart: evenement.clientX,
      yDepart: evenement.clientY,
      zone: depart && { ...depart },
    };
  }, [zones, memoriser]);

  const deplacer = useCallback((evenement) => {
    const g = geste.current;
    if (!g?.zone) return;
    const libre = evenement.altKey;
    const dx = (evenement.clientX - g.xDepart) / g.cadre.width;
    const dy = (evenement.clientY - g.yDepart) / g.cadre.height;
    const lignes = lignesParPage?.[g.zone.page] || [];

    modifier((zone) => {
      if (zone.id !== g.id) return zone;
      if (!g.poignee) {
        const x = aimanter(borne(g.zone.x + dx, 0, 1 - zone.largeur), libre);
        let y = aimanter(borne(g.zone.y + dy, 0, 1 - zone.hauteur), libre);
        y = aimanterALaLigne(y, zone.hauteur, lignes, libre);
        return { ...zone, x, y };
      }
      const taille = redimensionner(g.zone, g.poignee, dx, dy);
      return {
        ...zone,
        x: borne(aimanter(taille.x, libre), 0, 1 - DIMENSION_MIN),
        y: borne(aimanter(taille.y, libre), 0, 1 - DIMENSION_MIN),
        largeur: borne(aimanter(taille.largeur, libre), DIMENSION_MIN, 1 - taille.x),
        hauteur: borne(aimanter(taille.hauteur, libre), DIMENSION_MIN, 1 - taille.y),
      };
    });
  }, [modifier, lignesParPage]);

  const finir = useCallback(() => {
    geste.current = null;
    setEnDeplacement(null);
  }, []);

  /**
   * Le clavier : flèches = 1 px, Maj+flèches = 10 px, Suppr supprime, Ctrl+Z annule.
   *
   * ⚠️ CE N'EST PAS QU'UNE QUESTION D'ACCESSIBILITÉ. Personne ne pose une zone au pixel à
   * la souris : c'est le geste qu'on fait vraiment quand on veut que le nom tombe
   * exactement sur la ligne, après l'avoir approché à la souris.
   */
  const auClavier = useCallback((evenement) => {
    if ((evenement.ctrlKey || evenement.metaKey) && evenement.key.toLowerCase() === 'z') {
      evenement.preventDefault();
      annuler();
      return;
    }
    if (!selection) return;
    if (evenement.key === 'Delete' || evenement.key === 'Backspace') {
      evenement.preventDefault();
      supprimer(selection);
      return;
    }
    const pas = evenement.shiftKey ? PAS_CLAVIER_RAPIDE : PAS_CLAVIER;
    const vecteurs = {
      ArrowLeft: [-pas, 0], ArrowRight: [pas, 0],
      ArrowUp: [0, -pas], ArrowDown: [0, pas],
    };
    const vecteur = vecteurs[evenement.key];
    if (!vecteur) return;
    evenement.preventDefault();
    poserZones((actuelles) => {
      memoriser(actuelles);
      return actuelles.map((zone) => (zone.id === selection
        ? { ...zone,
            x: borne(zone.x + vecteur[0], 0, 1 - zone.largeur),
            y: borne(zone.y + vecteur[1], 0, 1 - zone.hauteur) }
        : zone));
    });
  }, [selection, supprimer, annuler, memoriser]);

  const changerChamp = useCallback((id, cle, valeur) => {
    poserZones((actuelles) => {
      memoriser(actuelles);
      return actuelles.map((z) => (z.id === id ? { ...z, [cle]: valeur } : z));
    });
  }, [memoriser]);

  return {
    zones,
    selection,
    setSelection,
    enDeplacement,
    poser,
    supprimer,
    annuler,
    peutAnnuler: pile.current.length > 0,
    commencer,
    deplacer,
    finir,
    auClavier,
    changerChamp,
    poserZones,
  };
}

export const CONSTANTES = {
  PAS_GRILLE, AIMANT_LIGNE, PAS_CLAVIER, PAS_CLAVIER_RAPIDE,
  LARGEUR_DEFAUT, HAUTEUR_DEFAUT, DIMENSION_MIN, PROFONDEUR_ANNULATION,
};
